import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getProducts, createProduct, updateProduct, updateProductStock, deleteProduct } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  Plus, 
  Package, 
  AlertTriangle,
  Loader2,
  Edit,
  Minus,
  PlusCircle,
  Trash2
} from 'lucide-react';
import { toast } from 'sonner';

const categories = [
  'Bebestibles',
  'Accesorios',
  'Alimentos',
  'Ropa',
  'Otros'
];

const Inventario = () => {
  const { isDirection } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLowStockOnly, setShowLowStockOnly] = useState(false);
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [showStockDialog, setShowStockDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    price: '',
    stock: '',
    min_stock: '5'
  });
  const [stockChange, setStockChange] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showLowStockOnly]);

  const fetchProducts = async () => {
    try {
      const response = await getProducts(showLowStockOnly);
      setProducts(response.data);
    } catch (error) {
      
      toast.error('Error al cargar productos');
    } finally {
      setLoading(false);
    }
  };

  const handleNewProduct = () => {
    setFormData({
      name: '',
      category: '',
      price: '',
      stock: '',
      min_stock: '5'
    });
    setSelectedProduct(null);
    setShowNewDialog(true);
  };

  const handleEditProduct = (product) => {
    setFormData({
      name: product.name,
      category: product.category || '',
      price: product.price.toString(),
      stock: product.stock.toString(),
      min_stock: product.min_stock.toString()
    });
    setSelectedProduct(product);
    setShowNewDialog(true);
  };

  const handleSaveProduct = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const data = {
        ...formData,
        price: parseFloat(formData.price),
        stock: parseInt(formData.stock),
        min_stock: parseInt(formData.min_stock)
      };
      
      if (selectedProduct) {
        await updateProduct(selectedProduct.id, data);
        toast.success('Producto actualizado');
      } else {
        await createProduct(data);
        toast.success('Producto creado');
      }
      setShowNewDialog(false);
      fetchProducts();
    } catch (error) {
      
      toast.error('Error al guardar producto');
    } finally {
      setSaving(false);
    }
  };

  const handleStockChange = async (productId, quantity) => {
    try {
      await updateProductStock(productId, quantity);
      toast.success('Stock actualizado');
      fetchProducts();
    } catch (error) {
      
      toast.error('Error al actualizar stock');
    }
  };

  const handleBulkStockChange = async (e) => {
    e.preventDefault();
    if (!stockChange || !selectedProduct) return;
    
    setSaving(true);
    try {
      await updateProductStock(selectedProduct.id, parseInt(stockChange));
      toast.success('Stock actualizado');
      setShowStockDialog(false);
      setStockChange('');
      fetchProducts();
    } catch (error) {
      
      toast.error('Error al actualizar stock');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProduct = async (product) => {
    if (!window.confirm(`Eliminar "${product.name}"? Esta accion no se puede deshacer.`)) return;
    try {
      await deleteProduct(product.id);
      toast.success('Producto eliminado');
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar producto');
    }
  };

  const totalInventoryValue = products.reduce((sum, p) => sum + (p.stock * p.price), 0);
  const lowStockCount = products.filter(p => p.low_stock_alert).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full" data-testid="inventory-loading">
        <Loader2 className="h-8 w-8 animate-spin text-[#2B4257]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="inventory-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="page-title">Inventario</h1>
          <p className="page-subtitle">Gestión de productos de la tienda</p>
        </div>
        {isDirection() && (
          <Button 
            className="btn-primary gap-2" 
            onClick={handleNewProduct}
            data-testid="btn-new-product"
          >
            <Plus size={18} />
            Nuevo Producto
          </Button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card flex items-center gap-4">
          <div className="p-3 bg-[#F5F0E8] rounded-lg">
            <Package className="text-[#2B4257]" size={24} />
          </div>
          <div>
            <p className="text-2xl font-semibold">{products.length}</p>
            <p className="text-sm text-[#5C7083]">Productos</p>
          </div>
        </div>
        <div className="card flex items-center gap-4">
          <div className="p-3 bg-[#FAF0EC] rounded-lg">
            <AlertTriangle className="text-[#D98A6C]" size={24} />
          </div>
          <div>
            <p className="text-2xl font-semibold">{lowStockCount}</p>
            <p className="text-sm text-[#5C7083]">Stock Bajo</p>
          </div>
        </div>
        <div className="card flex items-center gap-4">
          <div className="p-3 bg-[#EDF5F0] rounded-lg">
            <Package className="text-[#4A7C59]" size={24} />
          </div>
          <div>
            <p className="text-2xl font-semibold">{formatCurrency(totalInventoryValue)}</p>
            <p className="text-sm text-[#5C7083]">Valor Total</p>
          </div>
        </div>
      </div>

      {/* Filter */}
      <div className="card">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={showLowStockOnly}
            onChange={(e) => setShowLowStockOnly(e.target.checked)}
            className="w-4 h-4 rounded border-[#E8E2D7] text-[#2B4257] focus:ring-[#2B4257]"
            data-testid="checkbox-low-stock"
          />
          <span className="text-[#1A2835]">Mostrar solo productos con stock bajo</span>
        </label>
      </div>

      {/* Products Table */}
      <div className="card p-0 overflow-hidden" data-testid="products-table">
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>Producto</th>
                <th>Categoría</th>
                <th>Precio</th>
                <th>Stock</th>
                <th>Stock Mín.</th>
                <th>Valor Total</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {products.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-12">
                    <Package className="mx-auto h-12 w-12 text-[#E8E2D7] mb-4" />
                    <p className="text-[#5C7083]">No hay productos registrados</p>
                  </td>
                </tr>
              ) : (
                products.map((product) => (
                  <tr key={product.id} data-testid={`product-row-${product.id}`}>
                    <td className="font-medium text-[#1A2835]">{product.name}</td>
                    <td>{product.category || '-'}</td>
                    <td>{formatCurrency(product.price)}</td>
                    <td>
                      <div className="flex items-center gap-2">
                        {isDirection() && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="btn-ghost p-1 h-7 w-7"
                            onClick={() => handleStockChange(product.id, -1)}
                            disabled={product.stock <= 0}
                            data-testid={`btn-decrease-${product.id}`}
                          >
                            <Minus size={14} />
                          </Button>
                        )}
                        <span className={`font-semibold min-w-[2rem] text-center ${
                          product.low_stock_alert ? 'text-[#C25953]' : 'text-[#1A2835]'
                        }`}>
                          {product.stock}
                        </span>
                        {isDirection() && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="btn-ghost p-1 h-7 w-7"
                            onClick={() => handleStockChange(product.id, 1)}
                            data-testid={`btn-increase-${product.id}`}
                          >
                            <PlusCircle size={14} />
                          </Button>
                        )}
                      </div>
                    </td>
                    <td>{product.min_stock}</td>
                    <td>{formatCurrency(product.total_value)}</td>
                    <td>
                      {product.low_stock_alert ? (
                        <span className="status-badge status-congelado flex items-center gap-1">
                          <AlertTriangle size={12} />
                          Stock Bajo
                        </span>
                      ) : (
                        <span className="status-badge status-activo">OK</span>
                      )}
                    </td>
                    <td>
                      <div className="flex items-center gap-2">
                        {isDirection() && (
                          <>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="btn-ghost p-2"
                              onClick={() => handleEditProduct(product)}
                              data-testid={`btn-edit-product-${product.id}`}
                            >
                              <Edit size={16} />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="btn-ghost p-2"
                              onClick={() => {
                                setSelectedProduct(product);
                                setStockChange('');
                                setShowStockDialog(true);
                              }}
                              data-testid={`btn-stock-${product.id}`}
                            >
                              <Package size={16} />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="btn-ghost p-2 text-[#C25953] hover:text-[#A63D3D] hover:bg-[#FBEAE9]"
                              onClick={() => handleDeleteProduct(product)}
                              data-testid={`btn-delete-product-${product.id}`}
                            >
                              <Trash2 size={16} />
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* New/Edit Product Dialog */}
      <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{selectedProduct ? 'Editar Producto' : 'Nuevo Producto'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveProduct} className="space-y-4" data-testid="product-form">
            <div>
              <Label className="form-label">Nombre *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="form-input"
                required
                data-testid="input-product-name"
              />
            </div>

            <div>
              <Label className="form-label">Categoría</Label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="form-input"
                data-testid="select-product-category"
              >
                <option value="">Seleccionar categoría</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="form-label">Precio *</Label>
                <Input
                  type="number"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  className="form-input"
                  min="0"
                  required
                  data-testid="input-product-price"
                />
              </div>
              <div>
                <Label className="form-label">Stock *</Label>
                <Input
                  type="number"
                  value={formData.stock}
                  onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                  className="form-input"
                  min="0"
                  required
                  data-testid="input-product-stock"
                />
              </div>
              <div>
                <Label className="form-label">Stock Mín.</Label>
                <Input
                  type="number"
                  value={formData.min_stock}
                  onChange={(e) => setFormData({ ...formData, min_stock: e.target.value })}
                  className="form-input"
                  min="0"
                  data-testid="input-product-min-stock"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowNewDialog(false)}
                className="btn-secondary"
              >
                Cancelar
              </Button>
              <Button 
                type="submit" 
                className="btn-primary" 
                disabled={saving || !formData.name || !formData.price}
                data-testid="btn-save-product"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {selectedProduct ? 'Guardar Cambios' : 'Crear Producto'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Stock Dialog */}
      <Dialog open={showStockDialog} onOpenChange={setShowStockDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Ajustar Stock</DialogTitle>
          </DialogHeader>
          {selectedProduct && (
            <form onSubmit={handleBulkStockChange} className="space-y-4" data-testid="stock-form">
              <div className="p-3 bg-[#F5F0E8] rounded-lg">
                <p className="text-sm text-[#5C7083]">Producto</p>
                <p className="font-medium">{selectedProduct.name}</p>
                <p className="text-sm">Stock actual: <span className="font-semibold">{selectedProduct.stock}</span></p>
              </div>

              <div>
                <Label className="form-label">Ajuste de Stock</Label>
                <Input
                  type="number"
                  value={stockChange}
                  onChange={(e) => setStockChange(e.target.value)}
                  className="form-input"
                  placeholder="Ej: 10 para agregar, -5 para quitar"
                  data-testid="input-stock-change"
                />
                <p className="text-xs text-[#5C7083] mt-1">
                  Use números positivos para agregar y negativos para quitar
                </p>
              </div>

              <div className="flex justify-end gap-3">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowStockDialog(false)}
                  className="btn-secondary"
                >
                  Cancelar
                </Button>
                <Button 
                  type="submit" 
                  className="btn-primary" 
                  disabled={saving || !stockChange}
                  data-testid="btn-confirm-stock"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Actualizar Stock
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Inventario;
